import { QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useRolesPermissionMapping } from '@/hooks/featues/useRoles'
import type { Subjects } from '@/libs/ability'
import { SUBJECTS } from '@/libs/ability'
import type { Organization, Permission } from '@/libs/interfaces'
import { authApi } from '@/services/api'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  logout as logoutAction,
  setCurrentEmployee,
  setCurrentOrganization,
  setCurrentStore,
  setLoading,
  setPermissions,
  setUser,
} from '@/store/slices/authSlice'
export const queryClient = new QueryClient()

const ACTION_ALIASES: Record<string, Permission['action']> = {
  assign: 'update', // assign:user:store → update User
  auth: 'manage', // auth:integration:fortnox → manage Integration
  checkin: 'update', // check-in:employee → update Employee
  checkout: 'update', // check-out:employee → update Employee
  create: 'create',
  delete: 'delete',
  edit: 'update',
  manage: 'manage',
  read: 'read',
  remove: 'delete',
  revoke: 'delete', // revoke:permission:role → delete Settings
  send: 'create', // send:sms → create Communication
  update: 'update',
  upload: 'create', // upload:file → create File
  view: 'read',
}

const SUBJECT_ALIASES: Record<string, Subjects> = {
  // Billing / Financial
  bill: SUBJECTS.Billing,
  billing: SUBJECTS.Billing,
  bills: SUBJECTS.Billing,
  carsale: SUBJECTS.Billing,
  feature: SUBJECTS.Billing,
  invoice: SUBJECTS.Billing,
  invoices: SUBJECTS.Billing,
  plan: SUBJECTS.Billing,
  revenue: SUBJECTS.Billing,
  sales: SUBJECTS.Billing,
  subscription: SUBJECTS.Billing,
  subscriptionplan: SUBJECTS.Billing,
  usagelog: SUBJECTS.Billing,

  // Campaign
  campaign: SUBJECTS.Campaign,
  campaigns: SUBJECTS.Campaign,

  // Category
  categories: SUBJECTS.Category,
  category: SUBJECTS.Category,
  productcategory: SUBJECTS.Category,
  servicecategory: SUBJECTS.Category,

  // Communication
  communication: SUBJECTS.Communication,
  communications: SUBJECTS.Communication,
  email: SUBJECTS.Communication,
  emails: SUBJECTS.Communication,
  message: SUBJECTS.Communication,
  messages: SUBJECTS.Communication,
  sms: SUBJECTS.Communication,

  // Company
  companies: SUBJECTS.Company,
  company: SUBJECTS.Company,

  // Customer
  customer: SUBJECTS.Customer,
  customers: SUBJECTS.Customer,

  // Dashboard
  dashboard: SUBJECTS.Dashboard,
  stats: SUBJECTS.Dashboard,

  // Driver → treated as Customer; driver's car → Vehicle
  driver: SUBJECTS.Customer,
  drivers: SUBJECTS.Customer,
  drivercar: SUBJECTS.Vehicle,

  // Employee
  employee: SUBJECTS.Employee,
  employees: SUBJECTS.Employee,
  employment: SUBJECTS.Employee,

  // File
  file: SUBJECTS.File,
  files: SUBJECTS.File,

  // Integration
  integration: SUBJECTS.Integration,
  integrations: SUBJECTS.Integration,

  // Job / Order / Booking
  booking: SUBJECTS.Job,
  bookings: SUBJECTS.Job,
  job: SUBJECTS.Job,
  jobs: SUBJECTS.Job,
  order: SUBJECTS.Job,
  orders: SUBJECTS.Job,

  // Leave
  absence: SUBJECTS.Leave,
  absencetype: SUBJECTS.Leave,
  leave: SUBJECTS.Leave,
  leaves: SUBJECTS.Leave,

  // LoanCar
  loancar: SUBJECTS.LoanCar,
  rentcar: SUBJECTS.LoanCar,
  rentalfleet: SUBJECTS.LoanCar,

  // Pricing
  pricing: SUBJECTS.Pricing,

  // Product
  product: SUBJECTS.Product,
  products: SUBJECTS.Product,
  producttag: SUBJECTS.Product,

  // Qualification
  qualification: SUBJECTS.Qualification,

  // Service
  service: SUBJECTS.Service,
  servicetag: SUBJECTS.Service,
  servicevariant: SUBJECTS.Service,
  services: SUBJECTS.Service,

  // Settings (roles, permissions, org, union agreements)
  globalrole: SUBJECTS.Settings,
  orderlistingstatus: SUBJECTS.Settings,
  organization: SUBJECTS.Settings,
  organizations: SUBJECTS.Settings,
  permission: SUBJECTS.Settings,
  permissions: SUBJECTS.Settings,
  role: SUBJECTS.Settings,
  roles: SUBJECTS.Settings,
  setting: SUBJECTS.Settings,
  settings: SUBJECTS.Settings,
  unionagreement: SUBJECTS.Settings,

  // Store
  store: SUBJECTS.Store,
  stores: SUBJECTS.Store,

  // User
  invite: SUBJECTS.User,
  invites: SUBJECTS.User,
  staff: SUBJECTS.User,
  user: SUBJECTS.User,
  users: SUBJECTS.User,

  // Vehicle
  vehicle: SUBJECTS.Vehicle,
  vehicles: SUBJECTS.Vehicle,

  // WorkLog
  payroll: SUBJECTS.WorkLog,
  salary: SUBJECTS.WorkLog,
  salaryslip: SUBJECTS.WorkLog,
  teamoverview: SUBJECTS.WorkLog,
  timesheet: SUBJECTS.WorkLog,
  timesheetapproval: SUBJECTS.WorkLog,
  timesheetcomment: SUBJECTS.WorkLog,
  timesheetentry: SUBJECTS.WorkLog,
  timesheets: SUBJECTS.WorkLog,
  worklog: SUBJECTS.WorkLog,
  worklogs: SUBJECTS.WorkLog,
  workspace: SUBJECTS.WorkLog,

  // All
  all: SUBJECTS.all,
}

const normalizePermissionToken = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z]+/g, '')

const toAbilityPermission = (value?: string): Permission | null => {
  if (!value) return null

  const parts = value
    .split(':')
    .map((part) => normalizePermissionToken(part))
    .filter(Boolean)

  if (!parts.length) return null

  // Find the first part that maps to a known action (works for both
  // "action:subject" and "subject:action" formats).
  let actionIndex = -1
  let action: Permission['action'] | undefined
  for (let i = 0; i < parts.length; i++) {
    const mapped = ACTION_ALIASES[parts[i]]
    if (mapped) {
      actionIndex = i
      action = mapped
      break
    }
  }

  // All parts except the action part are subject candidates.
  // Check adjacent-pair compounds first (e.g. "drivercar" before "driver"),
  // then fall back to individual parts.
  const resourceParts = parts.filter((_, i) => i !== actionIndex)
  const subjectCandidates = [
    ...resourceParts.flatMap((_, i) =>
      i + 1 < resourceParts.length ? [resourceParts[i] + resourceParts[i + 1]] : [],
    ),
    ...resourceParts,
  ]
  const subject = subjectCandidates.map((key) => SUBJECT_ALIASES[key]).find(Boolean)

  if (!action || !subject) return null

  return { action, subject }
}

const mapRolePermissionToAbilityPermission = ({
  permissionTitle,
  permDispName,
}: {
  permissionTitle?: string
  permDispName?: string
}) => toAbilityPermission(permissionTitle) ?? toAbilityPermission(permDispName)

export function useAuth() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    user,
    token,
    permissions,
    currentOrganization,
    currentEmployee,
    isAuthenticated,
    isLoading,
  } = useAppSelector((state) => state.auth)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const permissionScopeKey = `${currentOrganization?.orgID || ''}:${currentOrganization?.roleID || ''}`
  const previousPermissionScope = useRef<string>(permissionScopeKey)

  const {
    data: rolePermissions = [],
    isError: permissionsError,
    isLoading: isPermissionsLoading,
  } = useRolesPermissionMapping({
    params: {
      orgID: currentOrganization?.orgID,
      roleID: currentOrganization?.roleID,
    },
    enabled: Boolean(isAuthenticated && currentOrganization?.orgID && currentOrganization?.roleID),
  })

  const abilityPermissions = useMemo(
    () =>
      rolePermissions
        .map((permission) => mapRolePermissionToAbilityPermission(permission))
        .filter((permission): permission is Permission => Boolean(permission)),
    [rolePermissions],
  )

  const abilityPermissionsKey = useMemo(
    () => JSON.stringify(abilityPermissions),
    [abilityPermissions],
  )
  const currentPermissionsKey = useMemo(() => JSON.stringify(permissions), [permissions])

  useEffect(() => {
    if (!isAuthenticated || !currentOrganization?.orgID || !currentOrganization?.roleID) {
      previousPermissionScope.current = ''
      if (permissions.length > 0) {
        dispatch(setPermissions([]))
      }
      return
    }

    if (previousPermissionScope.current !== permissionScopeKey) {
      previousPermissionScope.current = permissionScopeKey
      if (permissions.length > 0) {
        dispatch(setPermissions([]))
      }
    }
  }, [
    currentOrganization?.orgID,
    currentOrganization?.roleID,
    dispatch,
    isAuthenticated,
    permissionScopeKey,
    permissions.length,
  ])

  useEffect(() => {
    if (!isAuthenticated || !currentOrganization?.orgID || !currentOrganization?.roleID) return
    if (isPermissionsLoading) return

    if (permissionsError) {
      dispatch(setPermissions([]))
      return
    }

    if (currentPermissionsKey !== abilityPermissionsKey) {
      dispatch(setPermissions(abilityPermissions))
    }
  }, [
    abilityPermissions,
    abilityPermissionsKey,
    currentOrganization?.orgID,
    currentOrganization?.roleID,
    currentPermissionsKey,
    dispatch,
    isAuthenticated,
    isPermissionsLoading,
    permissions.length,
    permissionsError,
  ])

  const login = useCallback(
    async (email: string, password: string) => {
      dispatch(setLoading(true))
      try {
        const response = await authApi.login({ email, password })
        const activeOrgs = response.orgs.filter((org: Organization) => org.isActive)
        const organization: Organization[] = activeOrgs.length > 0 ? activeOrgs : response.orgs
        if (!organization.length) {
          return { success: false, error: 'No organizations found' }
        }
        const stores = organization[0].stores ?? []
        localStorage.setItem('organizations', JSON.stringify(organization))
        dispatch(setCurrentOrganization(organization[0]))
        dispatch(setCurrentStore(stores[0] || null))
        dispatch(setUser(response.user))
        dispatch(setCurrentEmployee(organization[0].employees ?? null))
        navigate('/')
        return { success: true }
      } catch {
        return { success: false, error: 'Login failed' }
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, navigate],
  )

  const logout = useCallback(async () => {
    setIsLoggingOut(true)
    try {
      await authApi.logout()
    } finally {
      queryClient.removeQueries()
      queryClient.clear()
      localStorage.clear()
      sessionStorage.clear()
      dispatch(logoutAction())
      navigate('/login', { replace: true })
    }
  }, [dispatch, navigate])

  const switchOrganization = useCallback(
    (org: Organization) => {
      dispatch(setCurrentOrganization(org))
      dispatch(setCurrentStore(org.stores?.[0] ?? null))
      dispatch(setCurrentEmployee(org.employees ?? null))
      queryClient.clear()
    },
    [dispatch],
  )

  return {
    user,
    token,
    permissions,
    currentOrganization,
    currentEmployee,
    isAuthenticated,
    isLoading,
    isLoggingOut,
    login,
    logout,
    switchOrganization,
  }
}
